const LABELABLE = 'input:not([type="hidden"]), select, textarea';
const ERRORS = '[data-form-error]';
const HELP = '.form-group__help';
const LABEL = '[data-field-label]';
const LABEL_TEXT = '[data-field-label-text]';
const REQUIRED = 'data-field-required';

let fieldIds = 0;

const MANAGED_DESCRIPTION = /^elm-field-\d+-description-\d+$/;
const MANAGED_LABEL = /^elm-field-\d+-label$/;

function describe(control, descriptionIds) {
  // Ids we did not generate are the caller's and survive; the Set keeps a
  // preserved one from being appended again on every rewire.
  const ids = [
    ...new Set(
      (control.getAttribute('aria-describedby') ?? '')
        .split(' ')
        .filter((id) => id && !MANAGED_DESCRIPTION.test(id))
        .concat(descriptionIds),
    ),
  ];

  if (ids.length === 0) control.removeAttribute('aria-describedby');
  else control.setAttribute('aria-describedby', ids.join(' '));
}

// Only the name this component gave. A control named by anyone else keeps it.
function unname(control) {
  if (MANAGED_LABEL.test(control.getAttribute('aria-labelledby') ?? '')) {
    control.removeAttribute('aria-labelledby');
  }
}

class ElmField extends HTMLElement {
  connectedCallback() {
    this.fieldId ??= `elm-field-${++fieldIds}`;
    this.wire();
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  own(selector) {
    return Array.from(this.querySelectorAll(selector)).filter((el) => el.closest('elm-field') === this);
  }

  describedBy(node, controls) {
    for (let el = node.parentElement; el; el = el.parentElement) {
      const within = controls.filter((control) => el.contains(control));
      if (within.length === 1) return within;
      if (within.length > 1) return controls;
      if (el === this) break;
    }
    return controls;
  }

  wire() {
    const controls = this.own(LABELABLE);
    const described = new Map(controls.map((control) => [control, []]));

    this.own(`${ERRORS}, ${HELP}`).forEach((node, index) => {
      // Ours are positional and get reassigned every wire, so a block
      // appearing earlier cannot take an id another block is still using.
      // An id we did not mint is the caller's and is left alone.
      if (!node.id || MANAGED_DESCRIPTION.test(node.id)) {
        node.id = `${this.fieldId}-description-${index}`;
      }
      this.describedBy(node, controls).forEach((control) => described.get(control).push(node.id));
    });

    controls.forEach((control) => describe(control, described.get(control)));

    // FormGroup.required renders an asterisk and a class, neither of which
    // reaches assistive technology. The state belongs on the control.
    const required = this.hasAttribute(REQUIRED);
    controls.forEach((control) => {
      if (required) control.setAttribute('aria-required', 'true');
      else control.removeAttribute('aria-required');
    });

    const [label] = this.own(LABEL);
    if (!label) {
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      controls.forEach(unname);
      return;
    }

    const [labelText] = this.own(LABEL_TEXT);
    const [control] = controls;
    if (controls.length === 1 && !control.closest('label')) {
      control.id ||= `${this.fieldId}-control`;
      label.htmlFor = control.id;
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      if (labelText) {
        labelText.id ||= `${this.fieldId}-label`;
        control.setAttribute('aria-labelledby', labelText.id);
      } else {
        unname(control);
      }
    } else {
      const nameSource = labelText ?? label;
      nameSource.id ||= `${this.fieldId}-label`;
      label.removeAttribute('for');
      this.setAttribute('role', 'group');
      this.setAttribute('aria-labelledby', nameSource.id);
      // The field names the group now; a control still carrying our name from
      // when it was the only one would be announced twice.
      controls.forEach(unname);
    }
  }
}

if (!customElements.get('elm-field')) {
  customElements.define('elm-field', ElmField);
}
